import React, { useState, useEffect } from 'react';
import { FiCheckCircle, FiCircle } from 'react-icons/fi';
import GameLoader from './GameLoader';

function PlatformLoadingChecklist({
  dealsLoaded,
  companiesLoaded,
  investorsLoaded,
  backgroundLoading
}) {
  const [isMinimized, setIsMinimized] = useState(false);

  // Calculate data loading status (NLP is on-demand, not part of initial load)
  const dataLoaded = dealsLoaded && companiesLoaded && investorsLoaded;
  const platformFullyLoaded = dataLoaded && !backgroundLoading;

  // Auto-minimize after 3 seconds of being fully loaded
  useEffect(() => {
    if (platformFullyLoaded && !isMinimized) {
      const timer = setTimeout(() => {
        setIsMinimized(true);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [platformFullyLoaded, isMinimized]);

  const checklistSections = [
    {
      title: 'Data Loading',
      items: [
        {
          label: 'Deals',
          status: dealsLoaded ? 'complete' : (backgroundLoading ? 'loading' : 'pending')
        },
        {
          label: 'Companies',
          status: companiesLoaded ? 'complete' : (backgroundLoading ? 'loading' : 'pending')
        },
        {
          label: 'Investors',
          status: investorsLoaded ? 'complete' : (backgroundLoading ? 'loading' : 'pending')
        }
      ]
    }
  ];

  const getStatusIcon = (status) => {
    switch (status) {
      case 'complete':
        return <FiCheckCircle size={16} style={{ color: '#10b981', flexShrink: 0 }} />;
      case 'loading':
        return <GameLoader size={16} />;
      case 'pending':
        return <FiCircle size={16} style={{ color: '#d1d5db', flexShrink: 0 }} />;
      default:
        return <FiCircle size={16} style={{ color: '#d1d5db', flexShrink: 0 }} />;
    }
  };

  // Minimized version - compact badge
  if (platformFullyLoaded && isMinimized) {
    return (
      <div
        style={{
          marginTop: '16px',
          padding: '10px 14px',
          background: 'linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)',
          borderRadius: '8px',
          border: '2px solid #10b981',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '10px',
          cursor: 'pointer',
          transition: 'all 0.3s ease',
          animation: 'slideInFromBottom 0.5s ease'
        }}
        onClick={() => setIsMinimized(false)}
        onMouseOver={(e) => {
          e.currentTarget.style.transform = 'translateY(-2px)';
          e.currentTarget.style.boxShadow = '0 4px 12px rgba(16, 185, 129, 0.3)';
        }}
        onMouseOut={(e) => {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = 'none';
        }}
        title="Click to expand status"
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <FiCheckCircle size={18} color="#047857" />
          <div style={{
            fontSize: '13px',
            fontWeight: 700,
            color: '#047857'
          }}>
            Platform Ready
          </div>
        </div>
        <div style={{
          fontSize: '11px',
          fontWeight: 600,
          color: '#059669',
          opacity: 0.8
        }}>
          Click to view
        </div>
      </div>
    );
  }

  // Full version - detailed checklist
  return (
    <div style={{
      marginTop: '16px',
      padding: '16px',
      background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
      borderRadius: '8px',
      border: '1px solid #e2e8f0',
      transition: 'all 0.3s ease',
      animation: platformFullyLoaded ? 'slideUp 0.5s ease' : 'none'
    }}>
      <div style={{
        fontSize: '13px',
        fontWeight: 700,
        color: '#475569',
        marginBottom: '16px',
        textTransform: 'uppercase',
        letterSpacing: '0.5px'
      }}>
        Platform Status
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
        {checklistSections.map((section, sectionIndex) => (
          <div key={sectionIndex}>
            {/* Section Title */}
            <div style={{
              fontSize: '11px',
              fontWeight: 700,
              color: '#64748b',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              marginBottom: '8px'
            }}>
              {section.title}
            </div>

            {/* Section Items */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {section.items.map((item, itemIndex) => (
                <div key={itemIndex} style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '10px',
                  padding: '8px',
                  background: 'white',
                  borderRadius: '6px',
                  border: '1px solid #e2e8f0',
                  transition: 'all 0.2s ease'
                }}>
                  {getStatusIcon(item.status)}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                      fontSize: '13px',
                      fontWeight: 600,
                      color: item.status === 'complete' ? '#0f172a' : '#64748b',
                      marginBottom: item.subtitle ? '2px' : 0
                    }}>
                      {item.label}
                    </div>
                    {item.subtitle && (
                      <div style={{
                        fontSize: '11px',
                        color: '#94a3b8'
                      }}>
                        {item.subtitle}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Platform Fully Loaded Badge */}
      {platformFullyLoaded && (
        <div style={{
          marginTop: '12px',
          padding: '12px',
          background: 'linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)',
          borderRadius: '6px',
          border: '2px solid #10b981',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
          animation: 'fadeIn 0.5s ease'
        }}>
          <FiCheckCircle size={20} color="#047857" />
          <div style={{
            fontSize: '14px',
            fontWeight: 700,
            color: '#047857'
          }}>
            Platform Fully Loaded
          </div>
        </div>
      )}

      <style>
        {`
          @keyframes fadeIn {
            from { opacity: 0; transform: translateY(-10px); }
            to { opacity: 1; transform: translateY(0); }
          }
          @keyframes slideUp {
            from { max-height: 500px; opacity: 1; }
            to { max-height: 500px; opacity: 1; }
          }
          @keyframes slideInFromBottom {
            from { opacity: 0; transform: translateY(20px); }
            to { opacity: 1; transform: translateY(0); }
          }
        `}
      </style>
    </div>
  );
}

export default PlatformLoadingChecklist;
